import { Button, EmptyState, Skeleton, TypingIndicator } from '@bennows/boows-react';
import { useQuery } from '@tanstack/react-query';
import { History, Sparkles, SquarePen, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { fetchContext } from './assistant-api';
import { useAssistant } from './AssistantProvider';
import { ChatView } from './ChatView';
import { Composer } from './Composer';
import { ContextLine } from './ContextLine';
import { HistoryView } from './HistoryView';
import { contextForLocation } from './route-context';
import { SendErrorNotice } from './SendErrorNotice';
import { useAssistantChat } from './useAssistantChat';

const FOCUSABLE = 'button:not([disabled]), a[href], textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab and Shift+Tab inside the full-screen phone overlay: the last control wraps to the first and back. */
function trapTab(event: KeyboardEvent, root: HTMLElement | null) {
  if (!root) return;
  const controls = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => !element.closest('[hidden]'));
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === root)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * The docked assistant panel (spec 110 section 4.15.6): a complementary landmark next to the page on a desktop, a full-screen modal dialog (focus trapped) on a phone.
 * Focus moves in on open (the composer), Esc closes and the provider returns focus to the header button.
 * It has no approve or publish control; the credit line says so.
 */
export function AssistantPanel() {
  const { available, open } = useAssistant();
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (open) setOpened(true);
  }, [open]);
  // Mounted on the first open and kept (hidden) afterwards, so a running answer and the conversation survive a close.
  return available && opened ? <PanelBody /> : null;
}

function PanelBody() {
  const { t } = useTranslation();
  const { open, close: onClose, canRun, estimate, phone } = useAssistant();
  const location = useLocation();
  const routeContext = useMemo(() => contextForLocation(location.pathname, location.search), [location.pathname, location.search]);
  const [contextOff, setContextOff] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const context = useMemo(() => (contextOff ? { kind: 'none' as const } : routeContext), [contextOff, routeContext]);
  const chat = useAssistantChat(context);

  // A new page is a new subject: the removal of the page context applies to the page it was removed on.
  useEffect(() => setContextOff(false), [routeContext.kind, routeContext.id]);
  // Focus moves into the panel whenever it opens: the composer, or the panel itself when the member cannot write.
  const focusComposer = () => (panelRef.current?.querySelector<HTMLElement>('textarea:not([disabled])') ?? panelRef.current)?.focus();
  useEffect(() => {
    if (open) focusComposer();
  }, [open]);
  // Closing the history returns to the conversation; opening it leaves focus on the toggle that was pressed.
  const wasHistory = useRef(false);
  useEffect(() => {
    if (wasHistory.current && !historyOpen) focusComposer();
    wasHistory.current = historyOpen;
  }, [historyOpen]);
  const fillDraft = (text: string) => {
    setDraft(text);
    focusComposer();
  };

  const resolved = useQuery({ queryKey: ['assistant', 'context', context], queryFn: () => fetchContext(context), enabled: open, retry: false, staleTime: 60_000 });

  const send = async (text: string) => {
    if (sending) return;
    setSending(true);
    setDraft('');
    const started = await chat.send(text);
    setSending(false);
    // A message that did not start stays in the composer, so nothing typed is lost.
    if (!started) setDraft(text);
  };
  const newConversation = () => {
    chat.reset();
    setHistoryOpen(false);
    setDraft('');
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !event.defaultPrevented) {
      event.stopPropagation();
      onClose();
    } else if (event.key === 'Tab' && phone) {
      trapTab(event, panelRef.current);
    }
  };

  const last = chat.turns[chat.turns.length - 1];
  const hasContent = chat.turns.length > 0 || chat.active !== null;
  const tip = resolved.data?.tip ?? null;
  const chips = last ? last.suggestions : (resolved.data?.suggestions ?? []).map((chip) => t(chip.key, chip.params));
  const progress = chat.activeProgress;

  return (
    <aside
      id="assistant-panel"
      ref={panelRef}
      tabIndex={-1}
      className={phone ? 'ui-assistant ui-assistant--overlay' : 'ui-assistant'}
      role={phone ? 'dialog' : undefined}
      aria-modal={phone ? true : undefined}
      aria-labelledby="assistant-title"
      hidden={!open}
      onKeyDown={onKeyDown}
    >
      <div className="ui-assistant__header">
        <h2 id="assistant-title" className="ui-assistant__title">
          {t('assistant.title')}
        </h2>
        {(hasContent || chat.loading) && !historyOpen ? (
          <Button variant="ghost" iconOnly aria-label={t('assistant.newConversation')} onClick={newConversation}>
            <SquarePen aria-hidden="true" />
          </Button>
        ) : null}
        <Button variant="ghost" iconOnly aria-label={t(historyOpen ? 'assistant.historyClose' : 'assistant.historyOpen')} aria-pressed={historyOpen} onClick={() => setHistoryOpen((current) => !current)}>
          <History aria-hidden="true" />
        </Button>
        <Button variant="ghost" iconOnly aria-label={t('assistant.close')} onClick={onClose}>
          <X aria-hidden="true" />
        </Button>
      </div>

      {historyOpen ? (
        <div className="ui-assistant__body">
          <HistoryView
            onNew={newConversation}
            onOpen={(id) => {
              setHistoryOpen(false);
              void chat.open(id);
            }}
          />
        </div>
      ) : (
        <>
          <ContextLine loading={resolved.isPending} resolved={contextOff || resolved.isError ? null : (resolved.data ?? null)} removable={!contextOff} onRemove={() => setContextOff(true)} />
          <div className="ui-assistant__body">
            {chat.loading ? (
              <div role="status">
                <Skeleton variant="block" />
                <Skeleton variant="line" />
                <span className="ui-visually-hidden">{t('assistant.loading')}</span>
              </div>
            ) : chat.loadError ? (
              <EmptyState variant="error" title={t('assistant.historyError')} />
            ) : hasContent ? (
              <>
                <ChatView
                  turns={chat.turns}
                  activeText={chat.active?.text ?? null}
                  onRetry={chat.retry}
                  actionState={{ overrides: chat.overrides, conflicts: chat.conflicts, undoing: chat.undoing, onUndo: (action) => chat.undo(action) }}
                />
                {chat.active ? (
                  <div className="ui-assistant__working">
                    <TypingIndicator variant="shimmer" label={t('assistant.working')}>
                      {t('assistant.working')}
                    </TypingIndicator>
                    <ul className="ui-assistant__progress">
                      {progress.map((step, index) => (
                        <li key={`${step.tool}-${index}`}>{t(`assistant.progress.${step.state}`, { tool: t(`assistant.progress.tool.${step.tool}`, { defaultValue: step.tool }) })}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <EmptyState variant="compact" icon={<Sparkles aria-hidden="true" />} title={t('assistant.empty.title')} hint={t('assistant.empty.hint')} />
                {tip ? (
                  <div className="ui-assistant__tip">
                    <p className="ui-assistant__tip-title">{t('assistant.tip.title')}</p>
                    <p>{t(tip.finding_key)}</p>
                    <Button variant="secondary" size="sm" onClick={() => fillDraft(t(`assistant.tip.question.${tip.finding_key.split('.').pop()}`))}>
                      {t('assistant.tip.ask')}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
          {chat.sendError ? (
            <div className="ui-assistant__error">
              <SendErrorNotice error={chat.sendError} onNewConversation={newConversation} />
            </div>
          ) : null}
          <Composer
            value={draft}
            onChange={setDraft}
            onPick={fillDraft}
            onSend={(text) => void send(text)}
            busy={sending || chat.loading || chat.active !== null}
            disabled={!canRun}
            chips={chat.active ? [] : chips}
            estimate={estimate}
            lastCredits={last?.credits ?? null}
          />
        </>
      )}
    </aside>
  );
}

